import classNames from 'classnames';
import {observer} from 'mobx-react';
import React, {useEffect, useMemo, useState} from 'react';

import {AddToCartEvent, BuyablesAddedToCart, CartBuyable} from '@udemy/cart';
import {Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {udSentry} from '@udemy/sentry';
import {
    CertificationVoucherBuyable,
    SHOPPING_LIST_ACTIONS,
    ShoppingClient,
    ShoppingListActionsValues,
    ShoppingListName,
    ShoppingItem as UdemyShoppingItem,
} from '@udemy/shopping';
import {useFormatCurrency} from '@udemy/ud-data';

import {getConfig} from 'src/features/cart/config';

import {CertificationVoucherItem} from '../shopping-item/certification-voucher-item.react.component';
import shoppingItemStyles from '../shopping-item/shopping-item.module.less';
import {ShoppingItem} from '../shopping-item/shopping-item.react-component';
import {ActionHandlers, ActionSupportedLayoutsProps, CartItem} from '../shopping-item/types';
import styles from './certification-upsell.module.less';
import {useCertificationCourseUpsell} from './hooks/use-certification-course-upsell';
import {useCertificationVoucherUpsell} from './hooks/use-certification-voucher-upsell';
import {useCertificationUpsellCallback} from './hooks/use-notify-content-change';
import {useUpsellSelection} from './hooks/use-upsell-selection';
import {UpsellAddToCartAction} from './upsell-add-to-cart-action.react-component';
import {
    trackUpsellVoucherOfferPresented,
    trackUpsellVoucherOfferSelected,
} from './voucher-offer-events';

const EMPTY_ITEMS: UdemyShoppingItem[] = [];

export interface CertificationCourseUpsellProps {
    item: UdemyShoppingItem;
    index: number;
    listName: string;
    actions: ActionHandlers;
    actionsLayout?: ActionSupportedLayoutsProps;
    usesLatestDiscount?: (item: CartItem) => boolean;
    cartCourseIds?: Set<number>;
    cartVoucherOfferIds?: Set<string>;
    /**
     * When false, the component still mounts its hooks (so it can report status
     * via `onHasContent` / `onLoadingChange`) but renders nothing. The parent
     * section uses this to enforce its cascade: only the chosen row is
     * visible. Defaults to true so the component renders standalone.
     */
    isSelected?: boolean;
    /**
     * Called when this bundle's visible content changes.
     * `voucherOfferId` is passed so the parent section can deduplicate vouchers across bundles.
     * `shoppingItems` contains the full items currently shown (with prices) so the section can
     * compute totals and offer a bulk add-to-cart.
     */
    onHasContent?: (
        hasContent: boolean,
        voucherOfferId?: string,
        shoppingItems?: UdemyShoppingItem[],
    ) => void;
    /**
     * Called when this row's async dependencies (exam / bundle / course price)
     * change loading state. The parent section uses this to gate the cascade
     * fallback so it doesn't flicker between the course path and the voucher
     * path while data is still resolving.
     */
    onLoadingChange?: (isLoading: boolean) => void;
}

export interface TrackActionProps {
    action: ShoppingListActionsValues;
    item: UdemyShoppingItem;
    destListName?: string;
}

export interface CertificationUpsellSectionProps {
    listName: string;
    trackAction?: (props: TrackActionProps) => void;
    actionsLayout?: ActionSupportedLayoutsProps;
    usesLatestDiscount?: (item: CartItem) => boolean;
}

export interface CertificationVoucherUpsellProps {
    voucherItem: UdemyShoppingItem;
    index: number;
    listName: string;
    actions: ActionHandlers;
    actionsLayout?: ActionSupportedLayoutsProps;
    usesLatestDiscount?: (item: CartItem) => boolean;
    cartCourseIds?: Set<number>;
    /** See `CertificationCourseUpsellProps.isSelected`. */
    isSelected?: boolean;
    onHasContent?: (hasContent: boolean, shoppingItems?: UdemyShoppingItem[]) => void;
    /** See `CertificationCourseUpsellProps.onLoadingChange`. */
    onLoadingChange?: (isLoading: boolean) => void;
}

export const CertificationCourseUpsell: React.FC<CertificationCourseUpsellProps> = ({
    item,
    index,
    listName,
    actions,
    actionsLayout = 'horizontal',
    usesLatestDiscount = () => false,
    cartCourseIds,
    cartVoucherOfferIds,
    isSelected = true,
    onHasContent,
    onLoadingChange,
}) => {
    const onHasContentRef = useCertificationUpsellCallback(onHasContent);
    const onLoadingChangeRef = useCertificationUpsellCallback(onLoadingChange);

    const {
        resolved,
        otherCourseItem,
        showSuggestedCourse,
        showVoucher,
        hasContent,
        voucherOfferId,
        isLoading,
    } = useCertificationCourseUpsell({
        item,
        cartCourseIds,
        cartVoucherOfferIds,
    });

    useEffect(
        function notifyLoadingChange() {
            onLoadingChangeRef.current?.(isLoading);
        },
        [isLoading, onLoadingChangeRef],
    );

    useEffect(
        function notifyContentChange() {
            const shoppingItems: UdemyShoppingItem[] | undefined = hasContent
                ? [
                      ...(otherCourseItem ? [otherCourseItem] : []),
                      ...(showVoucher && resolved ? [resolved.voucherItem] : []),
                  ]
                : undefined;
            onHasContentRef.current?.(
                hasContent,
                hasContent ? voucherOfferId : undefined,
                shoppingItems,
            );
        },
        [hasContent, voucherOfferId, otherCourseItem, showVoucher, resolved, onHasContentRef],
    );

    if (!isSelected || !hasContent) return null;

    return (
        <div className={styles.items} data-testid="voucher-upsell-bundle">
            {showSuggestedCourse && otherCourseItem && (
                <div className={styles['suggested-course']}>
                    <ShoppingItem
                        item={otherCourseItem}
                        index={index}
                        listName={listName}
                        actions={actions}
                        actionsLayout={actionsLayout}
                        usesLatestDiscount={usesLatestDiscount}
                        renderActions={() => (
                            <UpsellAddToCartAction item={otherCourseItem} layout={actionsLayout} />
                        )}
                    />
                </div>
            )}
            {showVoucher && resolved && (
                <div className={styles.voucher}>
                    <CertificationVoucherItem
                        item={resolved.voucherItem}
                        index={index}
                        listName={listName}
                        actions={actions}
                        actionsLayout={actionsLayout}
                        usesLatestDiscount={usesLatestDiscount}
                        onImpression={() =>
                            trackUpsellVoucherOfferPresented(
                                resolved.voucherItem.buyable as CertificationVoucherBuyable,
                            )
                        }
                        renderActions={() => (
                            <UpsellAddToCartAction
                                item={resolved.voucherItem}
                                layout={actionsLayout}
                            />
                        )}
                    />
                </div>
            )}
        </div>
    );
};

export const CertificationVoucherUpsell: React.FC<CertificationVoucherUpsellProps> = ({
    voucherItem,
    index,
    listName,
    actions,
    actionsLayout = 'horizontal',
    usesLatestDiscount = () => false,
    cartCourseIds,
    isSelected = true,
    onHasContent,
    onLoadingChange,
}) => {
    const offerId = (voucherItem.buyable as CertificationVoucherBuyable).offer_id;

    const onHasContentRef = useCertificationUpsellCallback(onHasContent);
    const onLoadingChangeRef = useCertificationUpsellCallback(onLoadingChange);

    const {
        courseItems: visibleCourseItems,
        hasContent,
        isLoading,
    } = useCertificationVoucherUpsell({
        offerId,
        cartCourseIds,
    });

    const courseItemIdsKey = visibleCourseItems.map((item) => item.buyable.id).join(',');

    useEffect(
        function notifyLoadingChange() {
            onLoadingChangeRef.current?.(isLoading);
        },
        [isLoading, onLoadingChangeRef],
    );

    useEffect(
        function notifyContentChange() {
            onHasContentRef.current?.(hasContent, hasContent ? visibleCourseItems : undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hasContent, courseItemIdsKey],
    );

    if (!isSelected || !hasContent) return null;

    return (
        <div data-testid="voucher-first-upsell-bundle">
            {visibleCourseItems.map((courseItem, i) => {
                return (
                    <div key={courseItem.buyable.id} className={styles['suggested-course']}>
                        <ShoppingItem
                            item={courseItem}
                            index={index + i}
                            listName={listName}
                            actions={actions}
                            actionsLayout={actionsLayout}
                            usesLatestDiscount={usesLatestDiscount}
                            renderActions={() => (
                                <UpsellAddToCartAction item={courseItem} layout={actionsLayout} />
                            )}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export const CertificationUpsellSection: React.FC<CertificationUpsellSectionProps> = observer(
    ({listName, trackAction, actionsLayout = 'horizontal', usesLatestDiscount = () => false}) => {
        const {gettext, interpolate, ngettext} = useI18n();
        const {formatCurrency} = useFormatCurrency();
        const config = getConfig(gettext, interpolate, ngettext);
        const sectionTitle = config.strings.certificationVoucher.voucherUpsellSectionTitle;

        const cartItems = ShoppingClient.lists.cart?.items ?? EMPTY_ITEMS;

        const cartCourseItems = useMemo(
            () => cartItems.filter((item) => item.buyable.buyable_object_type === 'course'),
            [cartItems],
        );

        const cartCourseIds = useMemo(
            () => new Set(cartCourseItems.map((item) => item.buyable.id)),
            [cartCourseItems],
        );

        const cartVoucherItems = useMemo(
            () =>
                cartItems.filter(
                    (item): item is UdemyShoppingItem & {buyable: CertificationVoucherBuyable} =>
                        item.buyable.buyable_object_type === 'certification_voucher',
                ),
            [cartItems],
        );

        const cartVoucherOfferIds = useMemo(
            () => new Set(cartVoucherItems.map((item) => item.buyable.offer_id).filter(Boolean)),
            [cartVoucherItems],
        );

        const actions: ActionHandlers = useMemo(() => {
            const cartListName: ShoppingListName = 'cart';
            return {
                moveToList: (destName: ShoppingListName, item) => () => {
                    const inCart = ShoppingClient.lists.cart.items.some(
                        (i) =>
                            i.buyable.id === item.buyable.id &&
                            i.buyable.buyable_object_type === item.buyable.buyable_object_type,
                    );
                    if (inCart) {
                        return ShoppingClient.moveToList(cartListName, destName, item).then(() =>
                            trackAction?.({
                                action: SHOPPING_LIST_ACTIONS.MOVE,
                                item,
                                destListName: destName,
                            }),
                        );
                    }
                    if (destName === 'cart') {
                        return ShoppingClient.addToList(cartListName, [item.buyable]).then(() =>
                            trackAction?.({action: SHOPPING_LIST_ACTIONS.ADD, item}),
                        );
                    }
                    return Promise.resolve();
                },
                remove: (item: CartItem) => () =>
                    ShoppingClient.removeFromList(cartListName, item).then(() =>
                        trackAction?.({action: SHOPPING_LIST_ACTIONS.REMOVE, item}),
                    ),
            };
        }, [trackAction]);

        const {selected, isPending, isSelected, setLoading, setContent} = useUpsellSelection({
            cartCourseItems,
            cartVoucherItems,
        });

        const isUpsellHidden = !selected || isPending;

        const selectedShoppingItems = selected?.shoppingItems ?? EMPTY_ITEMS;
        const totalListPrice = selectedShoppingItems.reduce(
            (sum, i) => sum + (i.list_price?.amount ?? 0),
            0,
        );
        const totalPurchasePrice = selectedShoppingItems.reduce(
            (sum, i) => sum + (i.purchase_price?.amount ?? 0),
            0,
        );
        const totalListPriceString = totalListPrice === 0 ? '' : formatCurrency(totalListPrice);
        const totalPurchasePriceString =
            totalPurchasePrice === 0 ? '' : formatCurrency(totalPurchasePrice);
        const showTotalRow = selectedShoppingItems.length >= 1 && totalListPriceString !== '';
        const addToCartButtonLabel =
            selectedShoppingItems.length === 1
                ? gettext('Add to cart')
                : gettext('Add all to cart');

        const [isAddingAll, setIsAddingAll] = useState(false);
        const [addAllFailed, setAddAllFailed] = useState(false);

        async function handleAddAllToCart() {
            setIsAddingAll(true);
            try {
                await ShoppingClient.addToList(
                    'cart',
                    selectedShoppingItems.map((item) => item.buyable),
                );
                selectedShoppingItems.forEach((item) => {
                    ShoppingClient.lists.cart.add(item);
                });

                const trackingBuyables = selectedShoppingItems.map((item) => ({
                    type: item.buyable.buyable_object_type,
                    id: item.buyable.id,
                    trackingId: item.buyable.frontendTrackingId,
                })) as CartBuyable[];

                Tracker.publishEvent(
                    new AddToCartEvent({
                        buyables: trackingBuyables,
                        uiRegion: 'voucher_upsell_bundle',
                    }),
                );
                Tracker.publishEvent(
                    new BuyablesAddedToCart({
                        buyables: trackingBuyables,
                        uiRegion: 'voucher_upsell_bundle',
                    }),
                );
                selectedShoppingItems.forEach((item) => {
                    if (item.buyable.buyable_object_type === 'certification_voucher') {
                        trackUpsellVoucherOfferSelected(
                            item.buyable as CertificationVoucherBuyable,
                        );
                    }
                });
            } catch (e) {
                udSentry.captureException(e, {upsell_action: 'add_all_to_cart'});
                setAddAllFailed(true);
            } finally {
                setIsAddingAll(false);
            }
        }

        return (
            <div
                hidden={isUpsellHidden}
                className={styles.section}
                data-testid="voucher-upsell-bundles-section"
            >
                <h3 className={classNames('ud-heading-md', styles['section-title'])}>
                    {sectionTitle}
                </h3>
                {cartVoucherItems.map((voucherItem, idx) => (
                    <CertificationVoucherUpsell
                        key={`voucher-first-upsell-${voucherItem.buyable.id}`}
                        voucherItem={voucherItem}
                        index={idx}
                        listName={listName}
                        actions={actions}
                        actionsLayout={actionsLayout}
                        usesLatestDiscount={usesLatestDiscount}
                        cartCourseIds={cartCourseIds}
                        isSelected={isSelected('voucher', voucherItem.buyable.id)}
                        onLoadingChange={(isLoading) =>
                            setLoading('voucher', voucherItem.buyable.id, isLoading)
                        }
                        onHasContent={(hasContent, shoppingItems) =>
                            setContent('voucher', voucherItem.buyable.id, hasContent, shoppingItems)
                        }
                    />
                ))}
                {cartCourseItems.map((courseItem, idx) => (
                    <CertificationCourseUpsell
                        key={`voucher-upsell-${courseItem.buyable.id}`}
                        item={courseItem}
                        index={idx}
                        listName={listName}
                        actions={actions}
                        actionsLayout={actionsLayout}
                        usesLatestDiscount={usesLatestDiscount}
                        cartCourseIds={cartCourseIds}
                        cartVoucherOfferIds={cartVoucherOfferIds}
                        isSelected={isSelected('course', courseItem.buyable.id)}
                        onLoadingChange={(isLoading) =>
                            setLoading('course', courseItem.buyable.id, isLoading)
                        }
                        onHasContent={(hasContent, _voucherOfferId, shoppingItems) =>
                            setContent('course', courseItem.buyable.id, hasContent, shoppingItems)
                        }
                    />
                ))}
                {showTotalRow && (
                    <div className={styles['total-row']}>
                        <span className="ud-text-md">{gettext('Additional charge:')}</span>
                        <div className={styles['total-price-container']}>
                            <span
                                className={classNames(
                                    'ud-heading-md',
                                    shoppingItemStyles['price-text'],
                                )}
                            >
                                {totalPurchasePriceString}
                            </span>
                            {totalPurchasePrice < totalListPrice && (
                                <span
                                    className={classNames(
                                        'ud-text-sm',
                                        styles['total-list-price-strikethrough'],
                                    )}
                                >
                                    <s>{totalListPriceString}</s>
                                </span>
                            )}
                            {/* Using this button instead of the AddToCart component because it sets a window refresh when elements are added */}
                            <Button
                                udStyle="brand"
                                size="medium"
                                disabled={isAddingAll || addAllFailed}
                                onClick={handleAddAllToCart}
                            >
                                {addToCartButtonLabel}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    },
);
